/**
 * Manage User edit audit helpers.
 * Emits field labels only (no phone/email values) for HIPAA-safe trail text.
 *
 * Compare only fields the submit snapshot actually tracks. Apps that do not
 * edit a field (e.g. retail preferred language) must omit that key from
 * `current` so it is not false-flagged.
 */

const isEmpty = (value) =>
  value == null || value === '' || value === 'null' || value === 'undefined';

const normalizeText = (value) => (isEmpty(value) ? '' : String(value).trim());

const normalizeId = (value) => normalizeText(value);

const normalizePhone = (value) => normalizeText(value).replace(/\D/g, '');

/** Form default language; empty/null preferred_language is treated as this. */
const DEFAULT_LANGUAGE = 'en';

const normalizeLanguage = (value) => {
  const normalized = normalizeText(value).toLowerCase();
  return normalized || DEFAULT_LANGUAGE;
};

const hasOwn = (obj, key) =>
  !!obj && Object.prototype.hasOwnProperty.call(obj, key);

const roleId = (role) =>
  normalizeId(typeof role === 'object' && role ? role.id : role);

const roleName = (role) => {
  if (!role || typeof role !== 'object') {
    return normalizeText(role) || 'Unknown role';
  }
  return normalizeText(role.name || role.label || role.id) || 'Unknown role';
};

const toIdSet = (ids) => {
  const set = {};
  (ids || []).forEach((id) => {
    const normalized = normalizeId(id);
    if (normalized) {
      set[normalized] = true;
    }
  });
  return set;
};

const sortedRoleIds = (roles) =>
  (roles || [])
    .map(roleId)
    .filter(Boolean)
    .sort();

const filterRolesByScope = (roles, roleIdsInScope) => {
  if (!roleIdsInScope || !roleIdsInScope.length) {
    return roles || [];
  }
  const scope = toIdSet(roleIdsInScope);
  return (roles || []).filter((role) => scope[roleId(role)]);
};

const getOriginalEmail = (user) =>
  (user && (user.email || (user.contact && user.contact.email))) || '';

const getOriginalPhone = (user) =>
  (user && (user.phone || (user.contact && user.contact.phone))) || '';

const getOriginalLocationId = (user) =>
  (user &&
    user.preferredLocation &&
    user.preferredLocation.selected_location_id) ||
  '';

/**
 * @param {object} originalUser - user row before edit
 * @param {object} current - form snapshot at submit (only include editable fields)
 * @param {object} [options]
 * @param {Array<string|number>} [options.roleIdsInScope] - when set, only compare
 *   roles the editor can see/assign (avoids false "removed" for hidden roles)
 * @returns {{ changedFields: string[], roleAdded: string[], roleRemoved: string[] }}
 */
export const buildUserEditChanges = (originalUser, current, options) => {
  const opts = options || {};
  const source = originalUser || {};
  const target = current || {};
  const changedFields = [];
  const roleAdded = [];
  const roleRemoved = [];

  if (normalizeText(source.first_name) !== normalizeText(target.first_name)) {
    changedFields.push('first name');
  }
  if (normalizeText(source.last_name) !== normalizeText(target.last_name)) {
    changedFields.push('last name');
  }
  if (
    normalizeText(getOriginalEmail(source)).toLowerCase() !==
    normalizeText(target.email).toLowerCase()
  ) {
    changedFields.push('email');
  }
  if (normalizePhone(getOriginalPhone(source)) !== normalizePhone(target.phone)) {
    changedFields.push('phone');
  }
  if (normalizeText(source.badge_id) !== normalizeText(target.badge_id)) {
    changedFields.push('badge ID');
  }

  // Only when the snapshot explicitly tracks language (portal). Retail omits it.
  if (hasOwn(target, 'preferLanguage') || hasOwn(target, 'preferred_language')) {
    if (
      normalizeLanguage(source.preferred_language) !==
      normalizeLanguage(target.preferLanguage || target.preferred_language)
    ) {
      changedFields.push('preferred language');
    }
  }

  /*
   * Preferred location: if the user had no stored preferred location, the form
   * often seeds selectedBoxId to the first account location. Comparing empty
   * original vs seeded current would false-positive on every save. Only audit
   * when an original preferred location existed and the value changed, or when
   * an explicit baselineSelectedBoxId (dialog open value) differs from current.
   */
  if (hasOwn(target, 'selectedBoxId') || hasOwn(target, 'selected_location_id')) {
    const originalLoc = normalizeId(getOriginalLocationId(source));
    const currentLoc = normalizeId(
      target.selectedBoxId || target.selected_location_id
    );
    const baselineLoc = normalizeId(target.baselineSelectedBoxId);
    if (originalLoc) {
      if (originalLoc !== currentLoc) {
        changedFields.push('preferred location');
      }
    } else if (baselineLoc && currentLoc && baselineLoc !== currentLoc) {
      changedFields.push('preferred location');
    }
  }

  if (hasOwn(target, 'groupId') || hasOwn(target, 'group_id')) {
    if (
      normalizeId(source.group_id || (source.group && source.group.id)) !==
      normalizeId(target.groupId || target.group_id)
    ) {
      changedFields.push('group');
    }
  }

  if (hasOwn(target, 'parent_account_id')) {
    if (
      normalizeId(
        source.parent_account_id ||
          (source.parentAccount && source.parentAccount.id)
      ) !== normalizeId(target.parent_account_id)
    ) {
      changedFields.push('parent account');
    }
  }

  if (target.pictureChanged) {
    changedFields.push('profile image');
  }

  if (hasOwn(target, 'roles')) {
    const originalRoles = filterRolesByScope(
      source.roles || [],
      opts.roleIdsInScope
    );
    const currentRoles = filterRolesByScope(
      target.roles || [],
      opts.roleIdsInScope
    );
    const originalIds = sortedRoleIds(originalRoles);
    const currentIds = sortedRoleIds(currentRoles);
    const rolesChanged =
      originalIds.length !== currentIds.length ||
      originalIds.some((id, index) => id !== currentIds[index]);

    if (rolesChanged) {
      changedFields.push('roles');

      const originalById = {};
      originalRoles.forEach((role) => {
        const id = roleId(role);
        if (id) {
          originalById[id] = role;
        }
      });
      const currentById = {};
      currentRoles.forEach((role) => {
        const id = roleId(role);
        if (id) {
          currentById[id] = typeof role === 'object' ? role : { id: role };
        }
      });

      Object.keys(currentById).forEach((id) => {
        if (!originalById[id]) {
          roleAdded.push(roleName(currentById[id]));
        }
      });
      Object.keys(originalById).forEach((id) => {
        if (!currentById[id]) {
          roleRemoved.push(roleName(originalById[id]));
        }
      });
    }
  }

  return {
    changedFields: changedFields,
    roleAdded: roleAdded,
    roleRemoved: roleRemoved,
  };
};

/**
 * @returns {string|null} audit description, or null when nothing meaningful changed
 */
export const buildUserUpdatedAuditDescription = (
  userLabel,
  originalUser,
  current,
  options
) => {
  const changes = buildUserEditChanges(originalUser, current, options);
  if (!changes.changedFields.length) {
    return null;
  }

  const label = normalizeText(userLabel) || 'User';
  let description =
    "'" +
    label +
    "' user updated (changed: " +
    changes.changedFields.join(', ') +
    ')';

  const roleParts = [];
  if (changes.roleAdded.length > 0) {
    roleParts.push('added: ' + changes.roleAdded.join(', '));
  }
  if (changes.roleRemoved.length > 0) {
    roleParts.push('removed: ' + changes.roleRemoved.join(', '));
  }
  if (roleParts.length > 0) {
    description += ' [roles ' + roleParts.join('; ') + ']';
  }

  description += '.';
  return description;
};



// WEBPACK FOOTER //
// ./src/utils/userEditAuditFields.js